import { GEO_PATHS } from "../firebase";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DEFAULT_LAT, DEFAULT_LNG, DEFAULT_ZOOM } from "../constants/map";
import MapContainer from "../components/MapContainer";
import { getRadius, useGeoQuery, getGeoRecord, updateGeoRecord, deleteGeoRecord } from "../services/MapManager";
import AdvancedMarker from "../components/AdvancedMarker";
import { useResizeEffect } from "../services/useBreakpoint";
import Box from "@mui/material/Box";
import { partialType } from "../constants/partialType";
import pinRed from "../images/icons/pin-red.svg";
import pinBlue from "../images/icons/pin-blue.svg";
import pinGray from "../images/icons/pin-gray.svg";
import BaseModal from "../components/modals/BaseModal";
import { Grid, Typography } from "@mui/material";
import { TextItem } from "../components/TextItem";
import RenderUserRedirect from "../components/cellRender/RenderUserRedirect";
import { unixToString } from "../services/helper";
import StartIconButton from "../components/buttons/StartIconButton";
import RedoIcon from "@mui/icons-material/Redo";
import FormBuilder from "../components/FormBuilder";
import { FORM_TYPE } from "../constants/FormBuilder";
import HoverableIconButton from "../components/hoverables/HoverableIconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmIconButton from "../components/buttons/ConfirmIconButton";
import { debounce } from "lodash";
import { markerType } from "../constants/markerType";
import { USERS, get } from "services/Client";
import StatusIndicator from "../components/Statusindicator";
import EditIcon from "@mui/icons-material/Edit";
import { FIELD_TYPE, usePublicUtilityType, useReportType } from "../constants/publicUtilityRecord";

const ReportListLazy = React.lazy(() => import("./Dashboard/ReportList"));
const COMMUNITY_ORGANIZATION_ID = String('0');

/** Restituisce il GEO_PATH corretto in base al tipo di marker da mostrare */
function getGeoPath(markersToShow) {
    if (markersToShow === markerType.PARKING || markersToShow === markerType.REPAIRING_STATION)
        return GEO_PATHS.publicUtility;
    if (markersToShow === markerType.DRINKINGFOUNTAINS)
        return GEO_PATHS.drinkingFountains;
    if (markersToShow === markerType.REPORT)
        return GEO_PATHS.report;
    return null;
}

export default function PublicUtilityReportsMap({
    children,
    defaultCenter = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    defaultZoom = DEFAULT_ZOOM,
    markersToShow = markerType.DRINKINGFOUNTAINS,
    oid, disabled,
    isLayerVisible,
    cyclePaths,
    color
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const containerRef = useRef();
    const [reports, setReports] = useState(new Map());
    const [dim, setDim] = useState({ width: 1, height: 1 });
    const [report, setReport] = useState({});
    const [center, setCenter] = useState(defaultCenter);
    const [queryCriteria, setQueryCriteria] = useState({
        center: [defaultCenter.lat, defaultCenter.lng],
        zoom: defaultZoom
    });
    const [markers, setMarkers] = useState([]);

    const geoPath = getGeoPath(markersToShow);

    function radius() {
        return getRadius(queryCriteria.center, queryCriteria.zoom, dim.width, dim.height);
    }

    function getIcon(f) {
        let icon = { url: pinBlue, scaledSize: new window.google.maps.Size(35, 48) };
        if (f.deleted) {
            icon = { url: pinRed, scaledSize: new window.google.maps.Size(35, 48) };
        }
        return icon;
    }

    const getMarkerList = (reportsMap) => Array.from(reportsMap.values()).map((r) => {
        if (markersToShow === markerType.PARKING && r.type === 2) return null;
        if (markersToShow === markerType.REPAIRING_STATION && r.type === 1) return null;

        const markerProps = {
            position: { lat: r.l[0], lng: r.l[1] },
            key: r.key,
            icon: getIcon(r),
        };

        const allowClick = !(disabled && markersToShow === markerType.DRINKINGFOUNTAINS);
        if (allowClick) markerProps.onClick = () => setReport({ ...r });

        return <AdvancedMarker {...markerProps} />;
    });

    const updateReports = useCallback((newReports) => {
        if (!disabled) {
            newReports.forEach(r => {
                const creator = r.createdBy ?? r.uid;
                if (creator && creator !== '-') {
                    get(USERS, { elem: creator }).then((user) => {
                        r.createdBy = creator;
                        r.email = user.email;
                        r.username = user.username;
                    });
                } else {
                    r.createdBy = '-';
                    r.email = '-';
                    r.username = '-';
                }
            });
        }
        setReports(newReports);
        setMarkers(getMarkerList(newReports));
    }, [disabled, markersToShow]);

    // Callback key_entered: nuovo marker entra nel raggio
    const onKeyEntered = useCallback((key, data) => {
        // Filtra per organizzazione nel caso dei report
        if (markersToShow === markerType.REPORT) {
            if (![String(oid), COMMUNITY_ORGANIZATION_ID].includes(String(data.orgUid))) return;
        }
        // Non mostrare elementi eliminati se disabled
        if (disabled && data.deleted) return;

        setReports(prev => {
            if (prev.has(key)) return prev;
            const next = new Map(prev);
            next.set(key, { ...data, key });

            if (!disabled) {
                const creator = data.createdBy ?? data.uid;
                if (creator && creator !== '-') {
                    get(USERS, { elem: creator }).then((user) => {
                        setReports(r => {
                            const updated = new Map(r);
                            const item = updated.get(key);
                            if (item) {
                                updated.set(key, { ...item, email: user.email, username: user.username });
                                setMarkers(getMarkerList(updated));
                            }
                            return updated;
                        });
                    });
                }
            }

            setMarkers(getMarkerList(next));
            return next;
        });
    }, [markersToShow, oid, disabled]);

    // Callback key_exited: marker esce dal raggio
    const onKeyExited = useCallback((key) => {
        setReports(prev => {
            if (!prev.has(key)) return prev;
            const next = new Map(prev);
            next.delete(key);
            setMarkers(getMarkerList(next));
            return next;
        });
    }, [markersToShow]);

    // Reset quando cambia il tipo di marker da mostrare
    useEffect(() => {
        setReports(new Map());
        setMarkers([]);
    }, [markersToShow]);

    // Hook che sostituisce geoQuery.on("key_entered/exited")
    useGeoQuery(
        { center: queryCriteria.center, radius: radius() },
        [dim],
        geoPath,
        onKeyEntered,
        onKeyExited
    );

    useResizeEffect(() => {
        if (containerRef.current) {
            setDim({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
        }
    });

    // ─── Operazioni CRUD sul database ──────────────────────────────────────────

    const onRestore = (params) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        const update = { deleted: false, deletedAt: null, deletedBy: null };
        editFirebaseData(update, params);
    };

    const onEditStatus = (params) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        editFirebaseData({ deleted: params.deleted }, params);
    };

    function editFirebaseData(update, params) {
        updateGeoRecord(geoPath, params.key, update)
            .then(() => {
                setReports(prev => {
                    const next = new Map(prev);
                    next.set(params.key, { ...params, ...update });
                    setMarkers(getMarkerList(next));
                    return next;
                });
                enqueueSnackbar(t('saved'), { variant: "success" });
            })
            .catch(() => enqueueSnackbar(t('errorSavingData'), { variant: "error" }));
    }

    const onDelete = (params) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        deleteGeoRecord(geoPath, params.key)
            .then(() => {
                setReports(prev => {
                    const next = new Map(prev);
                    next.delete(params.key);
                    setMarkers(getMarkerList(next));
                    return next;
                });
                enqueueSnackbar(t('saved'), { variant: "success" });
            })
            .catch(() => enqueueSnackbar(t('errorSavingData'), { variant: "error" }));
    };

    const autocompleteAddressField = [{
        key: 'addressInput',
        label: t('address'),
        onCoordinatesFetched: (latLng) => {
            setReports(new Map());
            setCenter(latLng);
        },
        xs: 12, md: 12,
        noGlobalClasses: true,
        type: FORM_TYPE.addressAutocomplete
    }];

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '70vh',
                    '& .gmnoprint': { top: 'auto !important', bottom: 0 }
                }}
                ref={containerRef}
            >
                <Box sx={{
                    top: 0,
                    width: '100%',
                    zIndex: 100,
                    position: 'absolute',
                    padding: 2,
                    paddingX: 8,
                    '& .MuiOutlinedInput-root': { backgroundColor: 'white' }
                }}>
                    <FormBuilder fields={autocompleteAddressField} />
                </Box>
                <MapContainer
                    zoom={defaultZoom}
                    center={center}
                    onZoomChanged={(z) => setQueryCriteria((q) => ({ ...q, zoom: z }))}
                    onCenterChanged={(c) => setQueryCriteria((q) => ({ ...q, center: [c.lat(), c.lng()] }))}
                    isLayerVisible={isLayerVisible}
                    cyclePaths={cyclePaths}
                    color={color}
                >
                    {markers}
                    {children}
                </MapContainer>
            </Box>

            {!disabled && (
                <Box sx={{ mt: 4, width: "100%" }}>
                    <ReportListLazy reports={Array.from(reports.values())} onReportClick={(r) => setReport(r)} />
                </Box>
            )}

            {markersToShow === markerType.DRINKINGFOUNTAINS && (
                <EditFountainModal
                    fountain={report}
                    onClose={() => setReport({})}
                    open={!!report.key}
                    onDelete={onDelete}
                    onRestore={onRestore}
                />
            )}

            {(markersToShow === markerType.PARKING || markersToShow === markerType.REPAIRING_STATION) && (
                <PublicUtilityRecordModal
                    disabled={disabled}
                    record={report}
                    onClose={() => setReport({})}
                    open={!!report.key}
                    onDelete={onDelete}
                    onEditStatus={onEditStatus}
                />
            )}

            {markersToShow === markerType.REPORT && (
                <ReportModal
                    disabled={disabled}
                    record={report}
                    onClose={() => setReport({})}
                    open={!!report.key}
                    onDelete={onDelete}
                    onEditStatus={onEditStatus}
                />
            )}
        </>
    );
}

// ─── Modali (invariate) ────────────────────────────────────────────────────────

function EditFountainModal({ open, onClose, record = {}, onDelete, onEditStatus }) {
    const { t } = useTranslation();
    const cols = [
        { field: "createdAt", type: FIELD_TYPE.date, label: t("creationDate") },
        { field: "deletedAt", type: FIELD_TYPE.date, label: t("deletedAt") },
        { field: "deletedBy", type: FIELD_TYPE.textField, label: t("deletedBy") },
    ];
    return <BaseRecordModal open={open} onClose={onClose} record={record} column={cols} onDelete={onDelete} onEditStatus={onEditStatus} disabled={false} />;
}

function PublicUtilityRecordModal({ open, onClose, record = {}, onDelete, onEditStatus, disabled }) {
    const publicUtilityType = usePublicUtilityType();
    const { t } = useTranslation();
    const cols = [
        ...(disabled ? [] : [{ field: "uid", type: FIELD_TYPE.creator }]),
        { field: "createdAt", type: FIELD_TYPE.date, label: t("creationDate") },
        { field: "name", type: FIELD_TYPE.textField, label: t("name") },
        { field: "type", type: FIELD_TYPE.textField, label: t("type"), formatValue: (v) => publicUtilityType[v] },
    ];
    return <BaseRecordModal open={open} onClose={onClose} record={record} column={cols} onDelete={onDelete} onEditStatus={onEditStatus} disabled={disabled} />;
}

function ReportModal({ open, onClose, record = {}, onDelete, onEditStatus, disabled }) {
    const reportType = useReportType();
    const { t } = useTranslation();
    const cols = [
        ...(disabled ? [] : [{ field: "createdBy", type: FIELD_TYPE.creator }]),
        { field: "createdAt", type: FIELD_TYPE.date, label: t("creationDate") },
        { field: "notes", type: FIELD_TYPE.textField, label: t("notes") },
        { field: "type", type: FIELD_TYPE.textField, label: t("type"), formatValue: (v) => reportType[v] },
        { field: "imageUrl", type: FIELD_TYPE.image, label: t("image") },
    ];
    return (
        <BaseRecordModal
            open={open}
            onClose={onClose}
            record={record}
            column={cols}
            onDelete={onDelete}
            onEditStatus={onEditStatus}
            disabled={disabled}
        />
    );
}

function BaseRecordModal({ open, onClose, record = {}, column = [], onDelete, onEditStatus, disabled }) {
    const { t } = useTranslation();
    if (!open) return null;
    return (
        <BaseModal open={open} onClose={onClose}>
            <Grid container direction="column" spacing={2} alignItems="center" sx={{ width: "100%", mx: 0 }}>
                {column.map((c, k) => {
                    if (!record[c.field]) return null;
                    switch (c.type) {
                        case FIELD_TYPE.textField:
                            return (
                                <div key={k} style={{ width: "100%" }}>
                                    <TextItem xs={12} md={12} color="primary" label={c.label}
                                        value={c.formatValue ? c.formatValue(record[c.field]) : record[c.field]} />
                                </div>
                            );
                        case FIELD_TYPE.date:
                            return (
                                <div key={k} style={{ width: "100%" }}>
                                    <TextItem xs={12} md={12} color="primary" label={c.label}
                                        value={unixToString(record[c.field])} />
                                </div>
                            );
                        case FIELD_TYPE.image:
                            return (
                                <Grid item key={k}>
                                    <img style={{ maxWidth: "10em" }} src={record[c.field] || ""} alt="record" width="100%" />
                                </Grid>
                            );
                        case FIELD_TYPE.creator:
                            if (disabled) return null;
                            return (
                                <div key={k} style={{ width: '100%' }}>
                                    <TextItem xs={12} md={12} color={'primary'} label={t('creator')}
                                        value={
                                            <Box sx={{ '& button': { color: 'secondary.light' }, '& button:hover': { color: 'secondary.main' } }}>
                                                <RenderUserRedirect value={record[c.field]} url={record[c.field]} showUsername />
                                            </Box>
                                        }
                                    />
                                </div>
                            );
                        default:
                            return null;
                    }
                })}

                {!disabled && (
                    <>
                        <Grid item xs={12}>
                            <Grid container alignItems="center" sx={{ width: "100%" }}>
                                <Grid item xs={8}>
                                    <StatusIndicator isActive={!record.deleted} />
                                </Grid>
                                <Grid item xs={4}>
                                    <ConfirmIconButton
                                        onConfirm={() => { onEditStatus({ ...record, deleted: !record.deleted }); onClose(); }}
                                        title={t("status")}
                                        text={t("requestStatusChange")}
                                    >
                                        <EditIcon />
                                    </ConfirmIconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems="center">
                                <Grid item xs={8}>{t("remove")}</Grid>
                                <Grid item xs={4}>
                                    <ConfirmIconButton
                                        onConfirm={() => { onDelete(record); onClose(); }}
                                        title={t("confirmDeletion")}
                                        text={t("requestDefinitiveEliminationFountain")}
                                    >
                                        <DeleteIcon />
                                    </ConfirmIconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Grid>
        </BaseModal>
    );
}